import React, { useContext } from 'react'
import { useRouter } from 'next/router'
import {
  BenefitsTable,
  Faqs,
  Hero,
  ZigZag,
  PropValue,
  PriceTag,
  Quotes
} from '@pcln/vip'
import { ContentfulRichText, useContentful } from '@pcln/contentful-components'
import styled from 'styled-components'
import { Box, Text, Divider, Relative } from 'pcln-design-system'
import { EmailSignUpComponent } from '@/components/EmailSignUp'
import AppDownloadWrapper from '@/components/AppDownloadWrapper'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORY_VIP_LANDING } from '@/constants/analytics'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import isLoadedInAppView from '@/shared-utils/isLoadedInAppView'
import { QueryString, Articles } from '@/types'
import data from './data'
import NavBar from './NavBar'
import MemberCapture from './MemberCapture'
import Intro from './Intro'

const imageSrc =
  'https://s1.pclncdn.com/design-assets/vip/miniCooper-lg-100.jpg'

const heroContent = {
  headingText: 'Book more, save more.',
  text: 'Unlock huge savings, loyalty perks, and more when you become a VIP.'
}

function gaFAQ() {
  analytics.fireEvent({
    action: 'vip faq',
    category: ANALYTICS_CATEGORY_VIP_LANDING,
    label: 'faq click'
  })
}

function gaBenefits(
  _action: string,
  _swipeDirection: string,
  activeTab: string
) {
  analytics.fireEvent({
    action: 'vip benefits grid',
    category: ANALYTICS_CATEGORY_VIP_LANDING,
    label: `${activeTab} tab`
  })
}

const openModal = (redirectToLoginPage: boolean) => () => {
  if (redirectToLoginPage) {
    window.location.href = '/profile/#/login?return-url=/vip'
  } else {
    window?.PCLN?.HeaderClient?.openModal()
  }
}

function gaZigZag(id: number) {
  analytics.fireEvent({
    action: 'vip faq',
    category: ANALYTICS_CATEGORY_VIP_LANDING,
    label: `zig zag ${id} button`
  })
}

const categories = [
  {
    id: 0,
    label: 'VIP Benefits',
    anchor: 'benefits-table',
    icon: <PriceTag pr={2} fontSize="40px" variation="vip" />
  },
  {
    id: 1,
    label: 'VIP Overview',
    anchor: 'overview'
  },
  {
    id: 2,
    label: 'FAQs',
    anchor: 'faq'
  },
  {
    id: 3,
    label: 'Download the app',
    anchor: 'app-download'
  }
]

const handleZigZagClick = (redirectToLoginPage: boolean) => (id: number) => {
  openModal(redirectToLoginPage)()
  gaZigZag(id)
}

const ContentfulRichTextStyled = styled(ContentfulRichText)`
  margin: 0px;
`

export default function LandingPage() {
  const { isMobile = false, isTablet = false } =
    useContext(BootstrapDataContext)

  const FAQHeadlineFallback = 'Frequently Asked Questions'

  const articles = useContentful('articles') as Articles
  const faqs = articles?.items
    .filter(item => item?.body && item?.headline)
    .map(item => ({
      question: item.headline,
      answer: <ContentfulRichTextStyled body={item.body} />
    }))

  const { query } = useRouter()
  const isPageInAppView = isLoadedInAppView(query as QueryString)
  const redirectToLoginPage = isMobile || isTablet || isPageInAppView

  return (
    <Box>
      <Box color="background.light">
        <Hero imageSrc={imageSrc} content={heroContent} />
        <Relative mt={[-6, null, 0]}>
          <NavBar
            categories={isPageInAppView ? categories.slice(0, -1) : categories}
          />
          <Intro openModal={openModal(redirectToLoginPage)} />
        </Relative>
        <Box px={[2, null, null, null, 5]} pt={4} mx={[2, null, null, null, 5]}>
          <BenefitsTable id="benefits-table" navigationHandler={gaBenefits} />
        </Box>
        <Box px={3} pt={[5, null, null, null, 3]} mt="-160px">
          <Text textAlign="center" fontSize={0} color="text.light">
            *Trips are considered complete one day after the trip concludes
          </Text>
        </Box>
        <MemberCapture openModal={openModal(redirectToLoginPage)} />
        <PropValue {...data.valueProp} />
      </Box>
      <Box id="overview" p={3} color="background.lightest">
        <ZigZag
          onClick={handleZigZagClick(redirectToLoginPage)}
          {...data.zigzag}
        />
        <Box mx={-3} py={5}>
          <Quotes
            showInCarouselMode={isMobile}
            maxWidth={1086}
            contents={data.quotes}
          />
        </Box>
        <Text id="faq" mb={5} fontSize={5} textAlign="center" color="primary">
          {articles?.headline || FAQHeadlineFallback}
        </Text>
        <Box px={[2, null, null, null, 5]}>
          <Faqs onClick={gaFAQ} faqs={faqs || data.faqsFallback} />
        </Box>
      </Box>
      <Divider borderColor="border.base" my={0} />
      <EmailSignUpComponent
        sourceId="LANDING_VIP"
        category={ANALYTICS_CATEGORY_VIP_LANDING}
      />

      {isPageInAppView ? null : (
        <>
          <Box id="app-download">
            <AppDownloadWrapper category={ANALYTICS_CATEGORY_VIP_LANDING} />
          </Box>
        </>
      )}
      <Divider borderColor="border.base" my={0} />
    </Box>
  )
}
